import TutorialGuidanceStepContent from '@/components/common/TutorialGuidanceStepContent.vue';
import { useFormat } from '@/composables/format';
import type { TutorialGuidanceDto, TutorialGuidanceStep } from '@onrunning/backstage-models';
import { driver, type Config, type DriveStep, type PopoverDOM } from 'driver.js';
import 'driver.js/dist/driver.css';
import { createApp, type App } from 'vue';
import { useI18n } from 'vue-i18n';

export function useTutorialGuidanceTour() {
  const { t } = useI18n();
  const { getTranslationFromTranslateField } = useFormat();
  const mountedApps: App[] = [];

  const renderStepContent = (popover: PopoverDOM, step: TutorialGuidanceStep) => {
    const container = document.createElement('div');
    popover.description.innerHTML = '';
    popover.description.appendChild(container);
    // Remove display:none that driver.js adds when description is empty
    popover.description.style.display = '';

    const app = createApp(TutorialGuidanceStepContent, {
      imageUrl: step.imageUrl,
      video: step.video,
      description: step.description ? getTranslationFromTranslateField(step.description) : '',
      title: step.title ? getTranslationFromTranslateField(step.title) : '',
      titleUrl: step.titleUrl,
      browserNotSupportedVideoText: t('tutorialGuidance.browser_not_supported_video'),
      playVideoAriaLabel: t('tutorialGuidance.play_video'),
    });

    app.mount(container);
    mountedApps.push(app);
  };

  const cleanupMountedApps = () => {
    mountedApps.forEach((app) => app.unmount());
    mountedApps.length = 0;
  };

  const mapStep = (step: TutorialGuidanceStep): DriveStep => ({
    element: step.element,
  });

  const transformSteps = ({
    steps = [],
  }: TutorialGuidanceDto): { driveSteps: DriveStep[]; originalSteps: TutorialGuidanceStep[] } => ({
    driveSteps: steps.map(mapStep),
    originalSteps: steps,
  });

  const startGuidanceTour = (guidance: TutorialGuidanceDto, callbacks?: Partial<Config>) => {
    const { driveSteps, originalSteps } = transformSteps(guidance);

    if (!driveSteps.length) {
      if (callbacks?.onDestroyed) {
        (callbacks.onDestroyed as () => void)();
      }

      return;
    }

    const driverObj = driver({
      showProgress: true,
      animate: true,
      steps: driveSteps,
      nextBtnText: t('tutorialGuidance.next_action'),
      prevBtnText: t('tutorialGuidance.previous_action'),
      doneBtnText: t('tutorialGuidance.done_action'),
      ...callbacks,
      onPopoverRender: (popover, opts) => {
        const activeIndex = opts.state.activeIndex ?? 0;
        const currentStep = originalSteps[activeIndex];
        if (currentStep) {
          renderStepContent(popover, currentStep);
        }
        callbacks?.onPopoverRender?.(popover, opts);
      },
      onDestroyed: (element, step, opts) => {
        cleanupMountedApps();
        callbacks?.onDestroyed?.(element, step, opts);
      },
    });

    driverObj.drive();
  };

  return { startGuidanceTour };
}
